import React from "react";

import { CirclePlus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";
import { Separator } from "@/src/components/ui/separator";
import { cn } from "@/src/lib/utils";

import { countMatchingRows } from "./dataTableFilters";
import { CONTROL_SURFACE_CLASS, getColumnLabel } from "./dataTableStyles";

import type { RowData } from "@tanstack/react-table";
import type { JSX } from "react";

import type { DataTableColumn, FilterOption } from "./dataTable.types";

interface DataTableFacetedFilterProps<TData extends RowData> {
  column: DataTableColumn<TData>;
  value: string | null;
  onChange: (value: string | null) => void;
  /** Faux en mode serveur : le tableau ne connaît pas les comptes. */
  showCounts?: boolean;
}

interface FacetedFilterOptionsProps<TData extends RowData> {
  column: DataTableColumn<TData>;
  optionList: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  showCounts: boolean;
}

// Rendu seulement menu ouvert : le calcul des facettes parcourt toutes les lignes.
function FacetedFilterOptions<TData extends RowData>({
  column,
  optionList,
  value,
  onChange,
  showCounts,
}: FacetedFilterOptionsProps<TData>): JSX.Element {
  const facetCountByValue = showCounts ? column.getFacetedUniqueValues() : null;

  return (
    <React.Fragment>
      {optionList.map((option) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={option.value === value}
          className="whitespace-nowrap"
          onCheckedChange={(isChecked) =>
            onChange(isChecked ? option.value : null)
          }
        >
          {option.icon}
          {option.label}
          {facetCountByValue ? (
            <span className="text-muted-foreground ml-auto pl-4 text-xs">
              {countMatchingRows(facetCountByValue, option.value)}
            </span>
          ) : null}
        </DropdownMenuCheckboxItem>
      ))}
    </React.Fragment>
  );
}

function DataTableFacetedFilter<TData extends RowData>({
  column,
  value,
  onChange,
  showCounts = false,
}: DataTableFacetedFilterProps<TData>): JSX.Element {
  const { t } = useTranslation("table");

  const title = getColumnLabel(column);
  const optionList = column.columnDef.meta?.filterOptions ?? [];
  const selectedOption = optionList.find((option) => option.value === value);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(CONTROL_SURFACE_CLASS, "border-dashed")}
          size="lg"
          variant="outline"
        >
          <CirclePlus />
          {title}
          {selectedOption ? (
            <React.Fragment>
              <Separator className="mx-1 h-4" orientation="vertical" />
              <Badge variant="secondary">
                {/* Le bouton impose size-4 à ses icônes : on force la taille de la pastille. */}
                {selectedOption.icon ? (
                  <span
                    className="inline-flex [&_svg]:size-3!"
                    data-testid="DataTableFacetedFilter-selectedIcon"
                  >
                    {selectedOption.icon}
                  </span>
                ) : null}
                {selectedOption.label}
              </Badge>
            </React.Fragment>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        aria-label={title}
        className="min-w-(--radix-dropdown-menu-trigger-width)"
      >
        <FacetedFilterOptions
          column={column}
          onChange={onChange}
          optionList={optionList}
          showCounts={showCounts}
          value={value}
        />
        {selectedOption ? (
          <React.Fragment>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center"
              onSelect={() => onChange(null)}
            >
              {t("dataTable.clearFilter")}
            </DropdownMenuItem>
          </React.Fragment>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default DataTableFacetedFilter;
